<template>
  <div class="delivery">
    <div class="delivery__line">
      <AmInputMask
        ref="postal_code"
        v-model="shippingData.postal_code"
        typeMask="fixed"
        :mask="'#####-###'"
        data-test="postal_code"
        class="input--custom delivery__line__item"
        :label="$t('steps.delivery.postalCode')"
        placeholder="______ - ___"
        :validity-state="statusPostalCodeValidate"
        :secondary-label="textPostalCodeValidate"
        :input-mode="'tel'"
        @input="searchCep()"
        @focusout.native="validatePostalCode"
      />
      <AmInput
        v-model="shippingData.city"
        :test-name="'city'"
        class="input--custom delivery__line__item"
        :label="$t('steps.delivery.city.label')"
        :placeholder="$t('steps.delivery.city.placeholder')"
        :validity-state="statusCityValidate"
        :secondary-label="textCityValidate"
        @focusout.native="validateCity"
      />
    </div>
    <div class="delivery__line">
      <AmInput
        v-model="shippingData.street"
        :test-name="'street'"
        class="input--custom delivery__line__item__full"
        :label="$t('steps.delivery.street.label')"
        :placeholder="$t('steps.delivery.street.placeholder')"
        :validity-state="statusStreetValidate"
        :secondary-label="textStreetValidate"
        @focusout.native="validateStreet"
      />
      <AmDropdown
        ref="cep"
        v-model="shippingData.state"
        :test-name="'state'"
        :options="states"
        class="input--custom dropdown--custom delivery__line__item__full"
        :label="$t('steps.delivery.state.label')"
        :validity-state="statusStateValidate"
        input-placeholder=""
        dropdownAbsolute
        :disabled="disabledState"
        @click="validateState('async')"
        @focusout.native="validateState"
      />
    </div>
    <div class="delivery__line">
      <AmInput
        ref="shipping_number"
        v-model="shippingData.number"
        v-mask="'XXXXXX'"
        :test-name="'number'"
        type="number"
        class="input--custom delivery__line__item"
        :label="$t('steps.delivery.number.label')"
        :placeholder="$t('steps.delivery.number.placeholder')"
        :validity-state="statusNumberValidate"
        :secondary-label="textNumberValidate"
        @focusout.native="validateNumber"
      />
      <AmInput
        v-model="shippingData.neighborhood"
        :test-name="'neighborhood'"
        class="input--custom delivery__line__item"
        :label="$t('steps.delivery.neighborhood.label')"
        :placeholder="$t('steps.delivery.neighborhood.placeholder')"
        :validity-state="statusNeighborhoodValidate"
        :secondary-label="textNeighborhoodValidate"
        @focusout.native="validateNeighborhood"
      />
    </div>
    <div class="delivery__line">
      <AmInput
        v-model="shippingData.complement"
        :test-name="'complement'"
        class="input--custom delivery__line__item__full__single"
        :label="$t('steps.delivery.complement.label')"
        :placeholder="$t('steps.delivery.complement.placeholder')"
      />
    </div>
  </div>
</template>

<script>
import replaceSpecialCharactersMixin from '@/components/mixins/replaceSpecialCharactersMixin'
import themeMixin from '@/components/mixins/themeMixin'
import queryPersistance from '@/components/mixins/queryPersistance'

import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'

export default {
  name: 'StepDelivery',
  mixins: [themeMixin, replaceSpecialCharactersMixin, queryPersistance],
  data() {
    return {
      shippingData: {
        isSearchingCep: false,
        postal_code: null,
        city: null,
        street: null,
        number: null,
        neighborhood: null,
        state: null,
        complement: null,
      },
      statusPostalCodeValidate: null,
      textPostalCodeValidate: '',
      statusCityValidate: null,
      textCityValidate: '',
      statusStreetValidate: null,
      textStreetValidate: '',
      statusNumberValidate: null,
      textNumberValidate: '',
      statusNeighborhoodValidate: null,
      textNeighborhoodValidate: '',
      statusStateValidate: null,
      disabledState: false,
    }
  },
  computed: {
    ...mapState('Checkout', {
      states: (state) => state.states,
      identity: (state) => state.cart?.customer,
      cart: (state) => state.cart,
    }),
    ...mapGetters('Checkout', ['getCurrentStep']),
  },
  watch: {
    shippingData: {
      handler() {
        try {
          const cep = this.replaceSpecialCharacters(this.shippingData.postal_code)

          if (cep && cep.length === 8) {
            this.disableStateField()
            this.focusChangeState()
            this.validatePostalCode()
          }

          if (cep.length < 8) {
            this.shippingData.state = null
            this.enableStateField()
          }

          return true
        } catch (error) {
          return error
        }
      },
    },
    identity: {
      immediate: true,
      deep: true,
      async handler(newValue) {
        const {
          postcode,
          address_city,
          address_street,
          address_street_number,
          address_street_district,
          address_state,
          address_street_complement,
        } = newValue

        this.shippingData = {
          postal_code: postcode,
          city: address_city,
          street: address_street,
          number: address_street_number,
          neighborhood: address_street_district,
          state: address_state,
          complement: address_street_complement,
        }
      },
    },
  },
  mounted() {
    this.SET_CURRENT_STEP(1)
  },
  methods: {
    ...mapActions('Customer', ['shipping', 'fetchCep']),
    ...mapMutations('Checkout', ['SET_CURRENT_STEP']),
    async getCep(handleFocusToNumberField) {
      try {
        const cep = this.shippingData.postal_code
        const data = await this.fetchCep(cep)
        this.disableStateField()

        this.shippingData.city = data.localidade
        this.shippingData.state = data.uf
        this.shippingData.street = data.logradouro
        this.shippingData.neighborhood = data.bairro

        // CALLBACK ALTERANDO FOCUS
        handleFocusToNumberField(data)

        if (data.erro) {
          this.statusPostalCodeValidate = 'error'
          this.textPostalCodeValidate = 'CEP inválido'
          throw new Error('CEP inválido')
        }

        this.statusPostalCodeValidate = 'success'
        this.textPostalCodeValidate = ''

        return true
      } catch (error) {
        this.enableStateField()
        return error
      }
    },
    searchCep() {
      const cep = this.replaceSpecialCharacters(this.shippingData.postal_code)
      if (cep && cep.length === 8 && !this.isSearchingCep) {
        this.isSearchingCep = true
        this.getCep((data) => this.handleFocusToNumberField(data))
      }
    },
    previousStep() {
      this.$router.push({ name: 'new.dataStep', query: { pl: this.getPl?.pl } })
    },
    async nextStep() {
      return new Promise((resolve, reject) => {
        const validate = this.validateData()
        const routeName = this.$route?.params?.newRoute || 'new.stepReviewAddress'

        if (validate) {
          this.shipping(this.shippingData).then(() => {
            this.SET_CURRENT_STEP(2)
            this.$router.push({ name: routeName, query: { pl: this.getPl?.pl } })
            resolve()
          })
        } else {
          reject()
        }
      })
    },
    handleFocusToNumberField(data) {
      this.isSearchingCep = false
      if (!data.erro) {
        setTimeout(() => {
          if (this.$refs?.shipping_number?.$el) {
            this.$refs.shipping_number.$el.querySelector('input').focus()
          }
        }, 1000)
      }
      this.validateData(true)
    },
    focusChangeState() {
      // Esse trecho de código resolve o problema de v-model do AmDropdown
      this.$refs.cep.changeFocus(true)
      this.$refs.cep.changeFocus(false)
    },
    validateData(focus = false) {
      let validNumber = () => false
      const validPostalCode = this.validatePostalCode()
      const validCity = this.validateCity()
      const validState = this.validateState()
      const validStreet = this.validateStreet()
      if (!focus) {
        validNumber = this.validateNumber()
      }
      const validNeighborhood = this.validateNeighborhood()
      const validAll =
        validPostalCode &&
        validCity &&
        validStreet &&
        validNumber &&
        validNeighborhood &&
        validState

      if (validAll) {
        return true
      }
      throw new Error(false)
    },
    validatePostalCode() {
      if (!this.shippingData.postal_code) {
        this.statusPostalCodeValidate = 'error'
        this.textPostalCodeValidate = this.$t('steps.validators.required')
        return false
      }

      const replacePostalCode = this.replaceSpecialCharacters(this.shippingData.postal_code)

      if (replacePostalCode.length < 8) {
        this.statusPostalCodeValidate = 'error'
        this.textPostalCodeValidate = 'CEP inválido'
        return false
      }

      this.statusPostalCodeValidate = 'success'
      this.textPostalCodeValidate = ''

      return true
    },
    validateCity() {
      if (!this.shippingData.city) {
        this.statusCityValidate = 'error'
        this.textCityValidate = this.$t('steps.validators.required')
        return false
      }

      this.statusCityValidate = 'success'
      this.textCityValidate = ''

      return true
    },
    /* eslint-disable */
    validateState(async = false) {
      const validate = () => {
        if (!this.shippingData.state) {
          this.statusStateValidate = 'error'
          return false
        }

        this.statusStateValidate = 'success'

        return true
      }
      if (async) {
        setTimeout(() => {
          return validate()
        }, 200)
      } else {
        return validate()
      }
    },
    /* eslint-enable */
    validateStreet() {
      if (!this.shippingData.street) {
        this.statusStreetValidate = 'error'
        this.textStreetValidate = this.$t('steps.validators.required')
        return false
      }

      this.statusStreetValidate = 'success'
      this.textStreetValidate = ''

      return true
    },
    validateNumber() {
      if (!this.shippingData.number) {
        this.statusNumberValidate = 'error'
        this.textNumberValidate = this.$t('steps.validators.required')
        return false
      }

      this.statusNumberValidate = 'success'
      this.textNumberValidate = ''

      return true
    },
    validateNeighborhood() {
      if (!this.shippingData.neighborhood) {
        this.statusNeighborhoodValidate = 'error'
        this.textNeighborhoodValidate = this.$t('steps.validators.required')
        return false
      }

      this.statusNeighborhoodValidate = 'success'
      this.textNeighborhoodValidate = ''

      return true
    },
    disableStateField() {
      this.disabledState = true
    },
    enableStateField() {
      this.disabledState = false
    },
  },
}
</script>

<style lang="scss" scoped>
.delivery {
  width: 100%;

  &__line {
    display: flex;
    justify-content: space-between;

    &__item:last-child {
      width: 100%;
      margin-left: 16px;
    }

    @include mobile {
      &__item:first-child {
        width: 80%;
      }
    }

    @include desktop {
      &__item:first-child {
        width: 50%;
      }
    }

    &__item__full {
      width: 100%;
    }

    &__item__full:last-child {
      width: 30%;
      margin-left: 16px;
    }

    &__item__full__single {
      width: 100%;
    }
  }

  &__complement {
    text-align: left;
    margin: 24px 0 55px 0;
    cursor: pointer;

    @include mobile {
      margin-bottom: 0;
    }
  }

  .dropdown--custom {
    ::v-deep {
      .additional-slot {
        display: none;
      }
    }
  }
}
</style>
